import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TIER_META, formatCurrency } from "./format";

const LIVE_STAGES = ["Intake", "Kickoff", "Production", "On Hold"];

function StageBadge({ stage, onHorizon }) {
  if (onHorizon) {
    return (
      <span className="inline-flex items-center rounded-full border border-border px-2.5 py-0.5">
        <span className="micro-label text-warm-600">On the Horizon</span>
      </span>
    );
  }
  const tone = {
    Production: "text-estate-700",
    Kickoff: "text-estate-700",
    Delivered: "text-green-700",
    Closed: "text-muted-foreground",
    "On Hold": "text-warm-600",
    Intake: "text-muted-foreground",
  }[stage] || "text-muted-foreground";
  return (
    <span className="inline-flex items-center rounded-full border border-border px-2.5 py-0.5">
      <span className={`micro-label ${tone}`}>{stage}</span>
    </span>
  );
}

export default function ProjectsPanel({ projects }) {
  const navigate = useNavigate();
  const [scope, setScope] = useState("live"); // live | all

  const rows = useMemo(() => {
    if (scope === "all") return projects;
    // "Live" = in delivery or being prepared — everything but Delivered/Closed.
    return projects.filter(
      (p) => p.on_horizon || LIVE_STAGES.includes(p.stage)
    );
  }, [projects, scope]);

  return (
    <div className="space-y-4">
      <div className="inline-flex items-center rounded-full border border-border bg-warm-100/60 p-0.5">
        {[
          { key: "live", label: `Live · ${projects.filter((p) => p.on_horizon || LIVE_STAGES.includes(p.stage)).length}` },
          { key: "all", label: `All · ${projects.length}` },
        ].map((o) => (
          <button
            key={o.key}
            onClick={() => setScope(o.key)}
            className={`px-3 py-1 rounded-full text-xs transition-all duration-250 ease-brand ${
              scope === o.key
                ? "bg-card text-foreground border border-border"
                : "text-muted-foreground hover:text-foreground border border-transparent"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left px-4 py-2.5"><span className="micro-label">Project</span></th>
              <th className="text-left px-4 py-2.5"><span className="micro-label">Stage</span></th>
              <th className="text-left px-4 py-2.5"><span className="micro-label">VEXOS stage</span></th>
              <th className="text-left px-4 py-2.5"><span className="micro-label">PM</span></th>
              <th className="text-right px-4 py-2.5"><span className="micro-label">Contract</span></th>
              <th className="text-right px-4 py-2.5"><span className="micro-label">Collected</span></th>
              <th className="text-right px-4 py-2.5"><span className="micro-label">Subscription</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="border-b border-border last:border-0 hover:bg-warm-50/50 transition-colors">
                <td className="px-4 py-3 min-w-[220px]">
                  <button
                    onClick={() => navigate(`/programs/${p.id}`)}
                    className="text-left group"
                  >
                    <p className="text-foreground group-hover:underline underline-offset-2">
                      {p.program_name || "Untitled project"}
                    </p>
                    <p className="text-xs text-muted-foreground">{p.client_name || "—"}</p>
                  </button>
                </td>
                <td className="px-4 py-3">
                  <StageBadge stage={p.stage} onHorizon={p.on_horizon} />
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  {p.vexos_stage || "—"}
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{p.pm_name || "—"}</td>
                {/* One-time contract money — the subscription column is the
                    recurring stream and is deliberately not summed into it. */}
                <td className="px-4 py-3 text-right tabular">
                  {p.on_horizon
                    ? <span className="opacity-40" title="Unsigned — not contracted money">({formatCurrency(p.contract_value)})</span>
                    : formatCurrency(p.contract_value)}
                </td>
                <td className="px-4 py-3 text-right tabular">{formatCurrency(p.cash_collected)}</td>
                <td className="px-4 py-3 text-right">
                  {p.subscription ? (
                    <>
                      <span className="tabular">{formatCurrency(p.subscription.monthly_amount)}/mo</span>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {(TIER_META[p.subscription.tier] || {}).label || p.subscription.tier} · {p.subscription.term_months} mo term
                      </p>
                    </>
                  ) : (
                    <span className="opacity-30">—</span>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-sm text-muted-foreground">
                  No VEXOS projects in this view.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
